/**
 * 蜜糖黄 · 阳光黄页 注册页
 *
 * 字段完全由 /register/config 动态下发渲染（文件/图片先上传拿 URL），
 * 提交直调 /auth/register；协议文案通过 getSinglePage 弹窗加载；短信验证可选。
 * 视觉：奶油底 + 琥珀注册卡，顶部品牌条。
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

// ==================== 类型 ====================

type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

interface RegisterParams {
  username: string;
  password: string;
  site_id: string;
  confirm_password?: string;
  [key: string]: string | undefined;
}

// ==================== API ====================

const getSiteId = () => localStorage.getItem("current_site_id") || "";

async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, {
    params: { site_id: siteId },
  });
}

async function registerApi(params: RegisterParams) {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", {
    method: "POST",
    params: { site_id: siteId },
    body: { phone },
  });
}

async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return request("/upload", { method: "POST", body: formData });
}

export default function HoneyRegisterPage() {
  const { siteName } = useSite();
  const { siteConfig } = useSiteConfig();
  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);
  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>({
    show: false,
    title: "",
    content: "",
    loading: false,
  });
  const navigate = useNavigate();
  const formDataRef = useRef<Record<string, string>>({});

  const displayName = siteConfig?.site_name || siteName || "分类信息站";

  // ── 加载注册配置 ──
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(getSiteId());
        const enabledFields = (config.fields || []).filter((f) => f.is_enabled);
        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => {
          initialData[field.field_name] = "";
        });
        if (config.sms_enabled) {
          initialData["phone"] = "";
          initialData["sms_code"] = "";
          setSmsEnabled(true);
        }
        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch {
        setError("加载表单配置失败，请刷新重试");
      } finally {
        setIsLoadingConfig(false);
      }
    }
    loadConfig();
  }, []);

  // ── 短信倒计时 ──
  useEffect(() => {
    if (smsCountdown <= 0) return;
    const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [smsCountdown]);

  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({ show: true, title: page?.title || title, content: page?.content || "暂无内容", loading: false });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);

  const closeAgreement = useCallback(() => {
    setAgreementModal({ show: false, title: "", content: "", loading: false });
  }, []);

  // ── 发送短信 ──
  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current["phone"] || "";
    if (!phone) {
      setError("请先输入手机号");
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError("手机号格式不正确");
      return;
    }
    try {
      setSmsLoading(true);
      await sendSmsCode(getSiteId(), phone);
      setSmsCountdown(60);
      setError("");
    } catch (err: any) {
      setError(err.message || "短信发送失败，请重试");
    } finally {
      setSmsLoading(false);
    }
  }, []);

  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  const togglePassword = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  // ── 文件上传 ──
  const handleFileUpload = useCallback(
    async (fieldName: string, file: File) => {
      try {
        setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
        const result = await uploadFile(file);
        handleChange(fieldName, result.url);
      } catch {
        alert("文件上传失败，请重试");
      } finally {
        setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [handleChange]
  );

  // ── 图标与输入样式 ──
  const getFieldIcon = (type: FieldType) => {
    switch (type) {
      case "text":
      case "number":
        return "ri-edit-line";
      case "textarea":
        return "ri-file-text-line";
      case "select":
      case "radio":
      case "checkbox":
        return "ri-list-check";
      case "password":
        return "ri-lock-line";
      case "date":
        return "ri-calendar-line";
      case "file":
      case "image":
        return "ri-image-add-line";
      default:
        return "ri-edit-line";
    }
  };

  const baseInputClass =
    "w-full pl-10 pr-4 py-3 bg-[#FFF9EC] border border-[#EAE1CB] rounded-xl text-sm text-[#23201A] placeholder-[#9A917E] focus:outline-none focus:ring-2 focus:ring-[#F0A500]/30 focus:border-[#F0A500] transition-all";

  const renderField = (field: RegisterField) => {
    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            rows={3}
            className="w-full px-4 py-3 bg-[#FFF9EC] border border-[#EAE1CB] rounded-xl text-sm text-[#23201A] placeholder-[#9A917E] focus:outline-none focus:ring-2 focus:ring-[#F0A500]/30 focus:border-[#F0A500] transition-all resize-none"
          />
        );

      case "select":
        return (
          <select
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          >
            <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  value === option ? "border-[#F0A500] bg-[#FFF3D6]" : "border-[#EAE1CB] bg-[#FFF9EC]"
                }`}
              >
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  className="w-4 h-4 accent-[#F0A500]"
                />
                <span className="text-sm text-[#4A453B]">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label key={option} className="flex items-center gap-2 p-3 rounded-xl border border-[#EAE1CB] bg-[#FFF9EC] cursor-pointer">
                <input
                  type="checkbox"
                  name={field.field_name}
                  value={option}
                  checked={value.split(",").includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(",") : [];
                    const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter((v) => v !== option);
                    handleChange(field.field_name, newValues.join(","));
                  }}
                  className="w-4 h-4 rounded accent-[#F0A500]"
                />
                <span className="text-sm text-[#4A453B]">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            key={field.field_name}
            type="date"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          />
        );

      case "number":
        return (
          <input
            key={field.field_name}
            type="number"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            className={baseInputClass}
          />
        );

      case "file":
      case "image":
        return (
          <div key={field.field_name}>
            <label
              htmlFor={`file-${field.field_name}`}
              className="flex flex-col items-center justify-center w-full px-4 py-6 bg-[#FFF9EC] border-2 border-dashed border-[#D9CDAA] rounded-xl cursor-pointer hover:bg-[#FFF3D6] hover:border-[#F0A500] transition-colors"
            >
              {uploadingFields[field.field_name] ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-[3px] border-[#F0A500] border-t-transparent rounded-full animate-spin mb-2" />
                  <span className="text-sm text-[#6E6658]">上传中…</span>
                </div>
              ) : value ? (
                <div className="flex flex-col items-center">
                  {field.field_type === "image" ? (
                    <img src={value} alt="预览" className="w-24 h-24 object-cover rounded-lg mb-2" onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }} />
                  ) : (
                    <i className="ri-file-check-line text-3xl text-[#B87A00] mb-2" />
                  )}
                  <span className="text-sm text-[#6E6658]">点击重新上传</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-[#C9BC95] mb-2" />
                  <span className="text-sm text-[#6E6658]">
                    {field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}
                  </span>
                  {field.hint && <span className="text-xs text-[#9A917E] mt-1">{field.hint}</span>}
                </div>
              )}
            </label>
            <input
              id={`file-${field.field_name}`}
              type="file"
              accept={field.field_type === "image" ? "image/*" : "*/*"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(field.field_name, file);
              }}
              className="hidden"
            />
            {value && (
              <button type="button" onClick={() => handleChange(field.field_name, "")} className="mt-2 text-sm text-[#B3261E] hover:underline cursor-pointer">
                清除
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="relative">
            <input
              key={field.field_name}
              type={isPassword ? (showPassword ? "text" : "password") : "text"}
              value={value}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
              className={isPassword ? `${baseInputClass} pr-11` : baseInputClass}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => togglePassword(field.field_name)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#8B8577] hover:text-[#B87A00] cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"} />
              </button>
            )}
          </div>
        );
    }
  };

  // ── 校验 ──
  const validate = () => {
    const currentData = formDataRef.current;
    if (!agreed) {
      setError("请先阅读并同意注册协议和隐私政策");
      return false;
    }
    for (const field of fields) {
      if (field.is_required && !(currentData[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`);
        return false;
      }
    }
    if (smsEnabled) {
      const phone = currentData["phone"] || "";
      const smsCode = currentData["sms_code"] || "";
      if (!phone) {
        setError("请输入手机号");
        return false;
      }
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        setError("手机号格式不正确");
        return false;
      }
      if (!smsCode) {
        setError("请输入短信验证码");
        return false;
      }
      if (smsCode.length !== 6) {
        setError("短信验证码为6位数字");
        return false;
      }
    }
    const usernameField = fields.find((f) => f.field_name === "username");
    if (usernameField && currentData.username) {
      if (!/^\w{3,20}$/.test(currentData.username)) {
        setError("用户名只能包含字母、数字和下划线，长度3-20位");
        return false;
      }
    }
    if (currentData.password && currentData.password.length < 6) {
      setError("密码长度至少6位");
      return false;
    }
    return true;
  };

  // ── 提交 ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;

    setIsLoading(true);
    try {
      const currentData = formDataRef.current;
      const params: RegisterParams = {
        username: currentData.username,
        password: currentData.password,
        site_id: getSiteId(),
        confirm_password: currentData.confirm_password || "",
      };
      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name)) {
          params[field.field_name] = currentData[field.field_name] ?? "";
        }
      });
      if (smsEnabled) {
        params.phone = currentData["phone"];
        params.sms_code = currentData["sms_code"];
      }
      await registerApi(params);
      navigate("/");
    } catch (err: any) {
      setError(err.message || "注册失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-[#FFF9EC] flex items-center justify-center">
        <div className="text-center">
          <span className="inline-block w-10 h-10 border-[3px] border-[#F0A500] border-t-transparent rounded-full animate-spin" />
          <p className="mt-3 text-sm text-[#8B8577]">表单加载中…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9EC] px-4 py-8">
      {/* 品牌顶条 */}
      <div className="max-w-md mx-auto flex items-center justify-between mb-6">
        <Link to="/" className="flex items-center gap-1.5 text-sm text-[#6E6658] hover:text-[#B87A00] transition-colors">
          <i className="ri-arrow-left-line text-base" />
          返回首页
        </Link>
        <Link to="/" className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-[#23201A] text-[#F0A500] flex items-center justify-center font-black text-sm">
            {(displayName || "S").charAt(0).toUpperCase()}
          </span>
          <span className="text-lg font-black text-[#23201A]">{displayName}</span>
        </Link>
      </div>

      {/* 标题 */}
      <div className="max-w-md mx-auto mb-5">
        <h1 className="text-2xl font-black text-[#23201A] tracking-tight">创建账户</h1>
        <p className="text-xs text-[#8B8577] mt-1.5">加入 {displayName}，开启便捷的分类信息服务</p>
      </div>

      {/* 注册卡 */}
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-[#EAE1CB] shadow-xl shadow-[#F0A500]/5 overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[#F0A500] via-[#FFBE30] to-[#F0A500]" />
        <div className="p-5 sm:p-7">
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map((field) => (
              <div key={field.field_name}>
                <label className="block text-[13px] font-bold text-[#4A453B] mb-1.5">
                  {field.field_label}
                  {field.is_required && <span className="text-[#B3261E]"> *</span>}
                </label>
                <div className="relative">
                  <i className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B87A00] text-base`} />
                  {renderField(field)}
                </div>
                {field.hint && <p className="mt-1 text-xs text-[#9A917E]">{field.hint}</p>}
              </div>
            ))}

            {/* 短信验证 */}
            {smsEnabled && (
              <>
                <div>
                  <label className="block text-[13px] font-bold text-[#4A453B] mb-1.5">
                    手机号 <span className="text-[#B3261E]">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B87A00]" />
                    <input
                      type="tel"
                      value={formData["phone"] || ""}
                      onChange={(e) => {
                        handleChange("phone", e.target.value);
                        setError("");
                      }}
                      placeholder="请输入手机号"
                      className="w-full pl-10 pr-28 py-3 bg-[#FFF9EC] border border-[#EAE1CB] rounded-xl text-sm text-[#23201A] placeholder-[#9A917E] focus:outline-none focus:ring-2 focus:ring-[#F0A500]/30 focus:border-[#F0A500] transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleSendSmsCode}
                      disabled={smsCountdown > 0 || smsLoading}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs bg-[#23201A] text-[#F0A500] rounded-lg hover:bg-[#3A3529] disabled:bg-[#EAE1CB] disabled:text-[#9A917E] transition-colors cursor-pointer whitespace-nowrap font-bold"
                    >
                      {smsLoading ? "发送中…" : smsCountdown > 0 ? `${smsCountdown}s后重发` : "获取验证码"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-[#4A453B] mb-1.5">
                    短信验证码 <span className="text-[#B3261E]">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#B87A00]" />
                    <input
                      type="text"
                      value={formData["sms_code"] || ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                        handleChange("sms_code", value);
                        setError("");
                      }}
                      placeholder="请输入6位验证码"
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-3 bg-[#FFF9EC] border border-[#EAE1CB] rounded-xl text-sm text-[#23201A] placeholder-[#9A917E] focus:outline-none focus:ring-2 focus:ring-[#F0A500]/30 focus:border-[#F0A500] transition-all tracking-widest"
                    />
                  </div>
                </div>
              </>
            )}

            {/* 错误 */}
            {error && (
              <p className="flex items-start gap-2 p-3 bg-[#FDE7E4] border border-[#F5C6C0] text-[#B3261E] text-xs rounded-xl">
                <i className="ri-error-warning-line shrink-0 mt-0.5" />
                {error}
              </p>
            )}

            {/* 协议 */}
            <div
              className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                agreed ? "border-[#F0A500] bg-[#FFF3D6]" : "border-[#EAE1CB] bg-[#FFF9EC]"
              }`}
              onClick={() => setAgreed(!agreed)}
            >
              <span
                className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                  agreed ? "border-[#F0A500] bg-[#F0A500]" : "border-[#C9BC95] bg-white"
                }`}
              >
                {agreed && <i className="ri-check-line text-[#23201A] text-xs font-black" />}
              </span>
              <p className="text-[13px] text-[#4A453B] leading-relaxed select-none">
                我已阅读并同意
                <span
                  className="text-[#B87A00] hover:underline mx-1 cursor-pointer font-semibold"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAgreement("register-agreement", "注册协议");
                  }}
                >
                  《注册协议》
                </span>
                和
                <span
                  className="text-[#B87A00] hover:underline mx-1 cursor-pointer font-semibold"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAgreement("privacy", "隐私政策");
                  }}
                >
                  《隐私政策》
                </span>
              </p>
            </div>

            {/* 提交 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#F0A500] text-[#23201A] font-black rounded-xl hover:bg-[#FFB520] transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm shadow-lg shadow-[#F0A500]/25 cursor-pointer"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  注册中…
                </span>
              ) : (
                "立即注册"
              )}
            </button>
          </form>

          <div className="mt-5 text-center text-sm">
            <span className="text-[#6E6658]">已有账户？</span>
            <Link to="/login" className="ml-1.5 text-[#B87A00] font-black hover:underline">
              立即登录
            </Link>
          </div>
        </div>
      </div>

      {/* 协议弹窗 */}
      {agreementModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#23201A]/60 backdrop-blur-sm p-4" onClick={closeAgreement}>
          <div className="bg-[#FFF9EC] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAE1CB] shrink-0">
              <h2 className="text-lg font-black text-[#23201A] flex items-center gap-2">
                <i className="ri-file-text-line text-[#B87A00]" />
                {agreementModal.title}
              </h2>
              <button
                onClick={closeAgreement}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[#8B8577] hover:bg-[#F0A500]/15 hover:text-[#B87A00] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <span className="w-8 h-8 border-[3px] border-[#F0A500] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none text-[#4A453B] leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: agreementModal.content }}
                />
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#EAE1CB] shrink-0 flex justify-end">
              <button
                onClick={closeAgreement}
                className="px-6 py-2.5 bg-[#F0A500] text-[#23201A] font-black rounded-xl hover:bg-[#FFB520] transition-colors text-sm cursor-pointer"
              >
                已了解，关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
